import { useEffect } from 'react';
import { useRouteError } from 'react-router';
import { Loader2, RefreshCw } from 'lucide-react';
import { StageFlowLogo } from './StageFlowLogo';
import { reportClientError } from '../data/report-error';

// Full-screen branded shell for the moments the app can't show real content:
// the auth bootstrap and the initial route-chunk download. Rendering this
// instead of nothing is what keeps a slow (or failed) startup from being a
// blank white page.
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#1e1b4b] p-4">
      <div className="flex items-center gap-3 mb-6">
        <StageFlowLogo className="w-12 h-12" />
        <div>
          <h1 className="text-[24px] tracking-tight text-white">StageFlow</h1>
          <p className="text-[13px] text-indigo-300">Theater Company Management</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export function AppLoading() {
  return (
    <Shell>
      <div className="flex items-center gap-2 text-[14px] text-indigo-300">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading StageFlow...
      </div>
    </Shell>
  );
}

// Root router error screen — catches route render errors and failed lazy
// chunk downloads (typically a tab that predates the latest deploy asking
// for hashed files that no longer exist). A reload picks up the new build.
export function AppLoadError() {
  const error = useRouteError();
  console.error('[StageFlow] Route error:', error);
  const message = error instanceof Error ? error.message : String(error);
  useEffect(() => {
    reportClientError({
      source: 'react',
      message,
      stack: error instanceof Error ? error.stack : undefined,
    });
  }, [error, message]);
  return (
    <Shell>
      <p className="text-[14px] text-indigo-200 mb-1 text-center max-w-md">
        Something went wrong while loading this page.
      </p>
      <p className="text-[12px] text-indigo-400 mb-5 text-center max-w-md break-words">{message}</p>
      <button
        onClick={() => window.location.reload()}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-400 text-indigo-950 text-[14px] hover:bg-amber-300 transition-colors"
      >
        <RefreshCw className="w-4 h-4" />
        Reload
      </button>
    </Shell>
  );
}
